import React, { useState, useEffect } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import DevNavBar from '../components/DevNavBar';

/**
 * Pages under /start that a converted client still legitimately visits: the NDA (portal's /nda
 * route redirects here) and the mid-transaction return pages, which navigate themselves once
 * they've confirmed what they're waiting on. Everything else under /start is lead-only.
 */
const CLIENT_ALLOWED_START_PATHS = [
  '/start/nda',
  '/start/nda-complete',
  '/start/contract-complete',
  '/start/checkout',
];

/**
 * Once their account flips to a client — which now only happens after Stripe confirms a completed
 * checkout — the Start flow is behind them and the portal is their home. Without this they land
 * back on the Start dashboard's "contract signed" panel, which knows nothing about their program.
 */
const StartLayout = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const checkAccountType = async () => {
      if (!currentUser) {
        setIsClient(false);
        return;
      }
      try {
        const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
        const linkedId = userSnap.exists()
          ? (userSnap.data().accountId || userSnap.data().clientId)
          : null;
        if (!linkedId) return;
        const accountSnap = await getDoc(doc(db, 'accounts', linkedId));
        const type = accountSnap.exists() ? accountSnap.data().type : null;
        if (!cancelled) setIsClient(type === 'Clients' || type === 'clients');
      } catch (error) {
        // Leave them on /start rather than bouncing them somewhere they may not have access to.
        console.error('StartLayout: could not check account type', error);
      }
    };
    checkAccountType();
    return () => { cancelled = true; };
  }, [currentUser]);

  const isAllowedForClient = CLIENT_ALLOWED_START_PATHS.some((path) =>
    location.pathname.startsWith(path)
  );

  if (isClient && !isAllowedForClient) {
    return <Navigate to="/portal" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <DevNavBar />
      <main>
        <Outlet />
      </main>
    </div>
  );
};

export default StartLayout;
